import React from "react";

import { DURATION_UNITS } from "../../../../utils/durations";
import DataTableColumnHeader from "../../../dataTable/DataTableColumnHeader";
import {
  dateTimeColumn,
  durationColumn,
} from "../../../dataTable/dataTableColumns";
import DataTableLinkCell from "../../../dataTable/DataTableLinkCell";
import { userEditPath } from "../../../routes/routes";

import type { TFunction } from "i18next";

import type { MediaDetailedView } from "../../../../api/StatsAPI.types";
import type { DataTableColumnDef } from "../../../dataTable/dataTable.types";

interface MediaDetailedViewsColumnsOptions {
  t: TFunction;
  projectSlug: string | undefined;
  canAccessUsers: boolean;
}

function hasUserLink(
  view: MediaDetailedView,
  canAccessUsers: boolean,
): boolean {
  return (
    canAccessUsers && view.user_status !== "deleted" && Boolean(view.user_uuid)
  );
}

export function getMediaDetailedViewHref(
  view: MediaDetailedView,
  projectSlug: string | undefined,
  canAccessUsers: boolean,
): string | undefined {
  return hasUserLink(view, canAccessUsers)
    ? userEditPath(projectSlug, view.user_uuid)
    : undefined;
}

export function mediaDetailedViewsColumns({
  t,
  projectSlug,
  canAccessUsers,
}: MediaDetailedViewsColumnsOptions): DataTableColumnDef<MediaDetailedView>[] {
  // Texte affiché = texte cherché : un utilisateur supprimé ne se trouve pas par son nom caché.
  const getUserLabel = (view: MediaDetailedView): string => {
    if (view.user_status === "deleted") return t("editor-users:user_deleted");
    if (view.user_uuid) return view.user_name;
    return t("user_unknown");
  };

  return [
    {
      id: "user_name",
      accessorFn: getUserLabel,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("user")} />
      ),
      meta: { label: t("user"), searchable: true },
      cell: ({ row, getValue }) => (
        <DataTableLinkCell
          href={getMediaDetailedViewHref(
            row.original,
            projectSlug,
            canAccessUsers,
          )}
        >
          {getValue<string>()}
        </DataTableLinkCell>
      ),
    },
    durationColumn<MediaDetailedView>(
      "playtime",
      t("view_duration"),
      DURATION_UNITS.SECONDS,
      "fastDuration",
      { width: "18%", minWidth: 225, align: "center" },
    ),
    {
      id: "play_percentage",
      // Un pourcentage vide compte pour 0 au tri.
      accessorFn: (view) => view.play_percentage ?? 0,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("view_percent")} />
      ),
      meta: {
        label: t("view_percent"),
        width: "15%",
        minWidth: 150,
        align: "center",
      },
      cell: ({ getValue }) => {
        const percentage = getValue<number>();
        return percentage > 0
          ? `${Math.round(percentage * 100) / 100} %`
          : null;
      },
    },
    dateTimeColumn<MediaDetailedView>("inserted_at", t("view_date"), {
      width: "23%",
      minWidth: 280,
    }),
  ];
}
